// The KAfrequency Method — 10 pillars that organize the ecosystem.
// Pillars are content-derived: each song, teaching, activation, and book
// is mapped by tag/kind heuristics. Adding new content requires no data
// entry — it lands under the right pillar automatically.

import { ECOSYSTEM, type EcosystemItem, type ItemKind } from "./ecosystem";

export type PillarId =
  | "presence"
  | "attention"
  | "discipline"
  | "breath"
  | "consciousness"
  | "creativity"
  | "music"
  | "wisdom"
  | "purpose"
  | "integration";

export interface Pillar {
  id: PillarId;
  name: string;
  essence: string;
  invitation: string;
  glyph: string;
  hue: "gold" | "ivory" | "emerald" | "violet";
}

export const PILLARS: readonly Pillar[] = [
  { id: "presence",      name: "Presence",      essence: "The interior room.",                 invitation: "Arrive fully in the body you already inhabit.", glyph: "◉", hue: "gold" },
  { id: "attention",     name: "Attention",     essence: "Sacred currency.",                   invitation: "Direct the mind the way a river is directed.",  glyph: "✧", hue: "gold" },
  { id: "discipline",    name: "Discipline",    essence: "Peace with structure.",              invitation: "Return to the practice on days you don't want to.", glyph: "◈", hue: "emerald" },
  { id: "breath",        name: "Breath",        essence: "The first instrument.",              invitation: "Let the breath teach the nervous system its own rhythm.", glyph: "◐", hue: "ivory" },
  { id: "consciousness", name: "Consciousness", essence: "The witness behind the witness.",    invitation: "Recognize the field that watches the field.",   glyph: "❖", hue: "violet" },
  { id: "creativity",    name: "Creativity",    essence: "The current made form.",             invitation: "Let something move through you into the world.", glyph: "✦", hue: "gold" },
  { id: "music",         name: "Music",         essence: "Instruction in frequency.",          invitation: "Listen the way you would receive teaching.",     glyph: "♪", hue: "ivory" },
  { id: "wisdom",        name: "Wisdom",        essence: "The teachings that outlast the teacher.", invitation: "Sit with the sentences that refuse to age.", glyph: "❋", hue: "gold" },
  { id: "purpose",       name: "Purpose",       essence: "The reason you were sent.",          invitation: "Turn what you love into what you refuse to abandon.", glyph: "◇", hue: "emerald" },
  { id: "integration",   name: "Integration",   essence: "Bringing it home.",                  invitation: "Weave the practice into ordinary hours.",        glyph: "❂", hue: "violet" },
] as const;

const PILLAR_BY_ID: Record<PillarId, Pillar> = Object.fromEntries(PILLARS.map((p) => [p.id, p])) as Record<PillarId, Pillar>;
export function getPillar(id: PillarId): Pillar | undefined { return PILLAR_BY_ID[id]; }

/** Deterministic mapping from any ecosystem node to its primary pillar. */
export function pillarForItem(item: EcosystemItem): PillarId {
  const tags = new Set(item.tags);
  const hay = (item.title + " " + item.subtitle + " " + (item.body ?? "")).toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => tags.has(n) || hay.includes(n));

  if (item.kind === "music") {
    if (has("breath", "prana", "inhale")) return "breath";
    if (has("meditation", "stillness", "presence")) return "presence";
    return "music";
  }
  if (item.kind === "book") {
    if (has("47 gates", "gate", "consciousness", "awareness")) return "consciousness";
    if (has("discipline", "training", "structure", "peace")) return "discipline";
    return "wisdom";
  }
  if (item.kind === "activation") {
    if (has("breath", "pranayama", "inhale", "exhale")) return "breath";
    if (has("meditation", "presence", "stillness", "sitting")) return "presence";
    if (has("focus", "attention", "concentration")) return "attention";
    if (has("discipline", "training", "consistency")) return "discipline";
    if (has("morning", "intention", "purpose", "vision")) return "purpose";
    if (has("integration", "evening", "review", "reflection")) return "integration";
    return "presence";
  }
  // wisdom
  if (has("purpose", "livelihood", "work", "calling", "sovereignty")) return "purpose";
  if (has("breath", "pranayama")) return "breath";
  if (has("attention", "focus", "concentration")) return "attention";
  if (has("discipline", "training", "consistency", "structure")) return "discipline";
  if (has("creativity", "art", "expression", "creation")) return "creativity";
  if (has("consciousness", "awareness", "witness")) return "consciousness";
  if (has("integration", "everyday", "ordinary")) return "integration";
  if (has("music", "song", "sound", "frequency")) return "music";
  return "wisdom";
}

/** All ecosystem nodes mapped to a given pillar, in ecosystem order. */
export function itemsForPillar(id: PillarId): EcosystemItem[] {
  return ECOSYSTEM.filter((i) => pillarForItem(i) === id);
}

/** Group items by kind for balanced pillar-page rails. */
export function groupByKind(items: EcosystemItem[]): Record<ItemKind, EcosystemItem[]> {
  const out: Record<ItemKind, EcosystemItem[]> = { music: [], wisdom: [], activation: [], book: [] };
  for (const i of items) out[i.kind].push(i);
  return out;
}

export const ACTIVE_PILLAR_KEY = "kaf:active-pillar";

export function getActivePillar(): PillarId | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(ACTIVE_PILLAR_KEY);
    return v && PILLAR_BY_ID[v as PillarId] ? (v as PillarId) : null;
  } catch { return null; }
}

export function setActivePillar(id: PillarId | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(ACTIVE_PILLAR_KEY, id);
    else window.localStorage.removeItem(ACTIVE_PILLAR_KEY);
  } catch { /* noop */ }
}
